import { useEffect } from 'react';
import { useLatest } from './useLatest';

type Target = Window | Document | HTMLElement | MediaQueryList | BroadcastChannel | null | undefined;

/**
 * Listen to `type` on `target` (default: window) while the component is mounted.
 *
 * The handler always sees the latest props and state; the listener is attached once per target, type
 * and options, not on every render. A null target listens to nothing (for example, before a ref is set).
 * Custom app events (`window.dispatchEvent(new CustomEvent(...))`) use this too.
 */
export function useEventListener<E extends Event = Event>(
    type: string,
    handler: (event: E) => void,
    target: Target = typeof window === 'undefined' ? undefined : window,
    options?: { capture?: boolean; passive?: boolean; once?: boolean },
): void {
    const latest = useLatest(handler);
    const capture = options?.capture;
    const passive = options?.passive;
    const once = options?.once;
    useEffect(() => {
        if (!target) return undefined;
        const listener = (event: Event) => latest.current(event as E);
        const listenerOptions = { capture, passive, once };
        target.addEventListener(type, listener, listenerOptions);
        return () => target.removeEventListener(type, listener, listenerOptions);
    }, [target, type, capture, passive, once, latest]);
}
